// Only send debug logs to CloudWatch if enabled via env variable
const ENABLE_DEBUG_LOGS = process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'false';

// Get RUM session ID if available (AWS CloudWatch RUM session)
const getRumSessionId = () => {
  try {
    // Method 1: Direct RUM API call
    if (window.rum && typeof window.rum.getSession === 'function') {
      const session = window.rum.getSession();
      if (session?.sessionId) return session.sessionId;
      if (session?.id) return session.id;
    }
    
    // Method 2: Try accessing internal RUM state
    if (window.rum && window.rum.config) {
      const config = window.rum.config;
      if (config.sessionId) return config.sessionId;
    }
    
    // Method 3: Try to get from AWS RUM cookie (cwr_s = CloudWatch RUM Session)
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split('; ');
      
      // AWS RUM uses 'cwr_s' cookie for session
      const rumCookie = cookies.find(row => row.startsWith('cwr_s='));
      if (rumCookie) {
        try {
          const cookieValue = rumCookie.split('=')[1];
          const decoded = JSON.parse(decodeURIComponent(cookieValue));
          if (decoded.sessionId) return decoded.sessionId;
          if (decoded.id) return decoded.id;
        } catch (e) {
          // Cookie might be in different format, try base64
          try {
            const decoded = JSON.parse(atob(rumCookie.split('=')[1]));
            if (decoded.sessionId) return decoded.sessionId;
          } catch (e2) {}
        }
      }
      
      // Method 4: Try 'aws-rum-session' cookie
      const awsRumSession = cookies.find(row => row.startsWith('aws-rum-session='));
      if (awsRumSession) {
        return awsRumSession.split('=')[1];
      }
    }
    
    // Method 5: Check localStorage
    if (typeof localStorage !== 'undefined') {
      const rumSession = localStorage.getItem('aws-rum-session') || 
                         localStorage.getItem('cwr_session_id');
      if (rumSession) return rumSession;
    }
    
  } catch (e) {
    console.debug('[RUM Logger] Error getting RUM session ID:', e);
  }
  return null;
};

// Get user ID directly (no hashing)
const getHashedUserId = (userId) => {
  return userId || null;
};

// Get current route/pathname
const getRoute = () => {
  try {
    return window.location.pathname || null;
  } catch (e) {
    return null;
  }
};

// Get browser/device info for additional context
const getBrowserInfo = () => {
  try {
    const ua = navigator.userAgent || '';
    return {
      userAgent: ua.substring(0, 200), // Truncate to avoid huge payloads
      language: navigator.language || null,
      platform: navigator.platform || null,
      screenWidth: window.screen?.width || null,
      screenHeight: window.screen?.height || null,
      viewportWidth: window.innerWidth || null,
      viewportHeight: window.innerHeight || null,
      online: navigator.onLine
    };
  } catch (e) {
    return null;
  }
};

// Core log function with enhanced schema - ALL FIELDS ALWAYS INCLUDED
const log = (level, message, extra = {}) => {
  // Only send debug logs if enabled
  if (level === 'debug' && !ENABLE_DEBUG_LOGS) return;
  
  // Extract special fields from extra
  const {
    component,
    action,
    userId,
    context,
    error,
    includeBrowserInfo,
    appSessionId, // Application's recording session ID
    appConversationId, // Application's conversation ID
    ...restExtra
  } = extra;
  
  // Merge restExtra into context if context not provided
  const finalContext = context || (Object.keys(restExtra).length > 0 ? restExtra : {});
  
  // Extract sessionId and conversationId from context if provided there
  const extractedSessionId = appSessionId || finalContext?.sessionId || null;
  const extractedConversationId = appConversationId || finalContext?.conversationId || null;
  
  // Build the comprehensive log data following RUM schema
  // ALL FIELDS ARE ALWAYS PRESENT (null if not available)
  const logData = {
    // Core fields - always present
    timestamp: new Date().toISOString(),
    level: level.toUpperCase(),
    message: message || '',
    
    // Component/feature identification
    component: component || null,
    route: getRoute(),
    action: action || null,
    
    // Session/flow tracking
    // 1. rumSessionId - AWS CloudWatch RUM session (browser session tracking)
    rumSessionId: getRumSessionId(),
    // 2. appSessionId - Application's recording session ID (optional, from context)
    appSessionId: extractedSessionId,
    // 3. appConversationId - Application's conversation ID (optional, from context)
    appConversationId: extractedConversationId,
    
    // User identification (hashed for privacy)
    userId: getHashedUserId(userId),
    
    // Additional context payload
    context: Object.keys(finalContext).length > 0 ? finalContext : null,
    
    // Error details - always present, null for non-error logs
    error: null
  };
  
  // Add error details for error/fatal logs
  if ((level === 'error' || level === 'fatal') && error) {
    if (error instanceof Error) {
      logData.error = {
        name: error.name || 'Error',
        message: error.message || String(error),
        stack: error.stack || null,
        cause: error.cause ? String(error.cause) : null
      };
    } else if (typeof error === 'object') {
      logData.error = {
        name: error.name || 'Error',
        message: error.message || JSON.stringify(error),
        stack: error.stack || null,
        cause: error.cause || null
      };
    } else {
      logData.error = {
        name: 'Error',
        message: String(error),
        stack: null,
        cause: null
      };
    }
  }
  
  // Optionally include browser info for debugging
  if (includeBrowserInfo) {
    logData.browserInfo = getBrowserInfo();
  }
  
  // Enhanced RUM availability check
  try {
    if (window.rum && typeof window.rum.recordEvent === 'function') {
      window.rum.recordEvent("consoleLog", logData);
    } else {
      // Fallback to console with structured output
      const consoleMethod = level === 'error' || level === 'fatal' ? 'error' : 
                           level === 'warn' ? 'warn' : 
                           level === 'debug' ? 'debug' : 'log';
      console[consoleMethod](`[RUM:${level.toUpperCase()}]`, message, logData);
    }
  } catch (err) {
    console.error('[RUM] Error recording event:', err, { level, message, logData });
  }
};

const logger = {
  debug: (message, extra) => log("debug", message, extra),
  info: (message, extra) => log("info", message, extra),
  warn: (message, extra) => log("warn", message, extra),
  error: (message, extra) => log("error", message, extra),
  fatal: (message, extra) => log("fatal", message, extra),
};

export default logger;